import {ApplicantProfile} from '../types';

import {JobSearchStatus, MatchingStatus, ProfileVisibility} from '../../../../domain/settings';
import {SupportedLanguages} from '../../../../types/SupportedLanguages';

/* eslint-disable */
const camelCase =
  (lang: SupportedLanguages) =>
  (data: any): ApplicantProfile => {
    const applicant = data.applicant;
    const applicant_profile = applicant.applicant_profile;
    const personal_information = applicant_profile.personal_information;
    return {
      applicantId: applicant.resource_id,
      firstName: personal_information.first_name,
      lastName: personal_information.last_name,
      email: personal_information.email,
      mobilePhoneNumber: personal_information.mobile_phone_number,
      treatmentPronoun: personal_information.treatment_pronoun,
      genderPronoun: personal_information.gender_pronoun,
      linkedInUrl: applicant_profile.social_links?.linkedin_url,
      xingUrl: applicant_profile.social_links?.xing_url,
      dreamJobDescription: applicant_profile.dream_job_description,
      profilePictureUrl: applicant_profile.profile_picture_url,
      skills: applicant_profile.skills,
      location: applicant_profile.personal_information.location,
      profileTitle: applicant_profile.profile_title,
      professionalExperience:
        applicant_profile.professional_experience?.map((experience: any) => ({
          id: experience.label,
          methodDepartmentCategoryId: experience.method_department_category_id,
          name: experience.name[lang],
          metadata: {
            yearsOfExperience: experience.years_of_experience,
            label: experience.label,
          },
        })) || [],
      languages:
        applicant_profile.languages?.data?.map((language: any) => {
          return {
            id: language.id,
            proficiency: language.proficiency,
          };
        }) || [],
      workExperience:
        applicant_profile.work_experience?.data?.map((workExperience: any) => {
          return {
            id: workExperience.resource_id,
            organizationName: workExperience.organization_name,
            organizationLogoUrl: workExperience.organization_logo_url,
            location: workExperience.location,
            roleName: workExperience.role_name,
            startMonth: workExperience.start_month,
            startYear: workExperience.start_year,
            endMonth: workExperience.end_month,
            endYear: workExperience.end_year,
            description: workExperience.description,
            expertise: workExperience.expertise,
            isCurrent: workExperience.is_current,
          };
        }) || [],
      education:
        applicant_profile.education?.data?.map((education: any) => {
          return {
            id: education.resource_id,
            schoolName: education.school_name,
            schoolLogoUrl: education.school_logo_url,
            degree: education.degree,
            fieldOfStudy: education.field_of_study,
            startMonth: education.start_month,
            startYear: education.start_year,
            endMonth: education.end_month,
            endYear: education.end_year,
          };
        }) || [],
      profileVisibility: applicant_profile.settings?.profile_visibility || ProfileVisibility.Public, // TODO: remove ?
      // TODO: remove ?
      jobSearchStatus: applicant_profile.settings?.job_search_status || JobSearchStatus.ActivelyLooking,
      matchingStatus: applicant_profile.settings?.matching_status || MatchingStatus.Active, // TODO: remove ?
      leadershipExperience: applicant_profile.leadership_experience,
      minSalaryValue: applicant_profile?.salary_min_value,
      maxSalaryValue: applicant_profile?.salary_max_value,
      earliestStartDate: applicant_profile?.earliest_start_date,
      documents:
        applicant_profile?.documents?.map((doc: any) => {
          return {
            resourceId: doc.resource_id,
            type: doc.type,
            fileName: doc.file_name,
            url: doc.url,
          };
        }) || [],
    };
  };

const snakeCase = (profile: ApplicantProfile) => {
  return {
    applicant: {
      applicant_profile: {
        personal_information: {
          first_name: profile.firstName,
          last_name: profile.lastName,
          treatment_pronoun: profile.treatmentPronoun,
          gender_pronoun: profile.genderPronoun,
          mobile_phone_number: profile.mobilePhoneNumber,
          location: profile.location,
        },
        profile_title: profile.profileTitle,
        dream_job_description: profile.dreamJobDescription,
        leadership_experience: profile.leadershipExperience,
        salary_min_value: profile.minSalaryValue,
        salary_max_value: profile.maxSalaryValue,
        profile_picture_url: profile.profilePictureUrl,
        earliest_start_date: profile.earliestStartDate,
        skills: (profile.skills || []).map((skill) => ({
          name: skill.name,
          category: skill.category,
        })),
        professional_experience: (profile.professionalExperience || []).map((entry: any) => ({
          label: entry.label,
          method_department_category_id: entry.methodDepartmentCategoryId,
          years_of_experience: entry.yearsOfExperience,
        })) as any[],
        settings: {
          profile_visibility: profile.profileVisibility,
          job_search_status: profile.jobSearchStatus,
          matching_status: profile.matchingStatus,
        },
        languages: (profile.languages || []).map((language) => ({
          id: language.id,
          proficiency: language.proficiency,
        })),
      },
    },
  };
};

const transformDataForUpdate = (profile: ApplicantProfile, update: Partial<ApplicantProfile>) => {
  const transformProfessionalExp = () => {
    const convertProfExp = (exp: any) => {
      return {
        label: exp?.metadata.label,
        methodDepartmentCategoryId: exp?.methodDepartmentCategoryId,
        yearsOfExperience: exp?.metadata.yearsOfExperience,
      };
    };

    if (update.professionalExperience) {
      return (update?.professionalExperience || []).map(convertProfExp);
    }

    return (profile.professionalExperience || []).map(convertProfExp);
  };

  // Transforms strings to booleans.
  if ((update?.leadershipExperience as unknown as any) === 'yes') {
    update.leadershipExperience = true;
  }

  if ((update?.leadershipExperience as unknown as any) === 'no') {
    update.leadershipExperience = false;
  }

  const values = {
    ...profile,
    ...update,
    professionalExperience: transformProfessionalExp() as any,
  };

  return values;
};

export const applicantProfileOverride = {
  snakeCase,
  camelCase,
  transformDataForUpdate,
};
